import { AuthSubscriptions } from '@hefe/auth';
import { t } from '@hefe/i18n';
import {
  AssignableType,
  BasketballGameStatus,
  BasketballSeasonPlanerGame,
  SelectOption,
  TaskEvent,
  TaskEventType,
  TaskableType,
} from '@hefe/models';
import {
  BasketballGamesSlice,
  BasketballRefereesSlice,
  CarsSlice,
  currentUserSelector,
} from '@hefe/store';
import { colors } from '@hefe/ui';
import {
  clubHasSubscription,
  formatDateTime,
  formatTime,
  fullUserName,
} from '@hefe/utils';
import {
  Col,
  Descriptions,
  Popover,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { useState } from 'react';
import {
  FaBolt,
  FaBus,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLocationArrow,
  FaTimesCircle,
  FaUser,
  FaUsers,
} from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import InlineEditInput from '../../../core/components/inline-edit-input/inline-edit-input.component';
import UserAvatar from '../../../core/components/user/user-avatar/user-avatar/user-avatar.component';
import TaskAssignmentSelectComponent from '../../../tasks/components/task-assignment-select/task-assignment-select.component';
import styles from './gameday-planer-list-item.component.module.scss';

interface GamedayPlanerListItemComponentProps {
  away?: boolean;
  game: BasketballSeasonPlanerGame;
}

export default function GamedayPlanerListItemComponent(
  props: GamedayPlanerListItemComponentProps
) {
  const { game } = props;

  const dispatch = useAppDispatch();
  const [savingReferee1, setSavingReferee1] = useState(false);
  const [savingReferee2, setSavingReferee2] = useState(false);
  const [savingCars, setSavingCars] = useState(false);

  const user = useSelector(currentUserSelector);
  const carsSelect = useSelector(CarsSlice.carsSelectSelector);
  const refereesSelect = useSelector(
    BasketballRefereesSlice.basketballRefereesSelectWithPendingSelector
  );

  const updateReferee = (value: number, refereeNumber: number) => {
    if (refereeNumber === 1) {
      setSavingReferee1(true);
    } else {
      setSavingReferee2(true);
    }
    dispatch(
      BasketballGamesSlice.setBasketballGameOwnReferee({
        gameId: game.id,
        data: {
          referee: refereeNumber,
          user_id: value,
        },
      })
    )
      .unwrap()
      .finally(() => {
        if (refereeNumber === 1) {
          setSavingReferee1(false);
        } else {
          setSavingReferee2(false);
        }
      });
  };

  const updateCars = (teamId: number, value: number[]) => {
    setSavingCars(true);
    dispatch(
      BasketballGamesSlice.setBasketballTeamGameCars({
        gameId: game.id,
        data: {
          team_id: teamId,
          car_ids: value,
        },
      })
    )
      .unwrap()
      .finally(() => {
        setSavingCars(false);
      });
  };

  const renderTime = (time: string) => {
    const Text = (
      <Typography.Text strong style={{ fontSize: 18 }}>
        {game.status === BasketballGameStatus.CANCELED
          ? t('canceled').toUpperCase()
          : time}
      </Typography.Text>
    );

    if (time === '00:00') {
      return (
        <Popover content={t('gameday_planer.time_pending')}>{Text}</Popover>
      );
    } else {
      return Text;
    }
  };

  const renderHomeContent = () => {
    return (
      <Descriptions column={1} size="small" bordered>
        {renderReferees()}
        {renderTasks('home')}
      </Descriptions>
    );
  };

  const renderAwayContent = () => {
    const teams = [];

    for (const team of game.team_details) {
      const cars = [];
      if (team.cars) {
        for (const car of team.cars) {
          const carName = car
            ? `${car?.manufacturer} ${car?.model}`
            : t('private_cars');
          cars.push(carName);
        }
      }

      cars.push(t('private_cars'));

      if (clubHasSubscription(AuthSubscriptions.second, user)) {
        teams.push(
          <Space key={team.id}>
            <Typography.Text>{team.name + ' - '}</Typography.Text>
            <InlineEditInput
              highlightingMode="self"
              component={Select}
              value={team.cars?.map((c) => c.id)}
              text={cars.join(', ')}
              loading={savingCars}
              onOk={(value) => updateCars(team.id, value)}
              componentProps={{
                options: carsSelect,
                mode: 'multiple',
                showSearch: true,
                filterOption: (input: string, option: SelectOption) =>
                  option?.label?.toLowerCase().includes(input?.toLowerCase()),
              }}
            />
          </Space>
        );
      } else {
        teams.push(
          <Space key={team.id}>
            <Typography.Text>
              {team.name + ' - ' + cars.join(', ')}
            </Typography.Text>
          </Space>
        );
      }
    }

    return (
      <Space direction="vertical">
        <Space>
          <FaLocationArrow size={14} />
          <Typography.Text>{game.gym}</Typography.Text>
        </Space>
        <Space>
          <FaBus size={14} />
          <Space direction="vertical">{teams}</Space>
        </Space>
      </Space>
    );
  };

  const renderStatusIcon = (status: number) => {
    const iconSize = 14;
    let icon = <FaCheckCircle size={iconSize} color={colors.green} />;
    switch (status) {
      case 1:
        icon = <FaTimesCircle size={iconSize} color={colors.red} />;
        break;
      case 2:
        icon = <FaExclamationTriangle size={iconSize} color={colors.orange} />;
    }

    return <Space>{icon}</Space>;
  };

  const renderReferees = () => {
    const referee1 = game.referee1 ?? t('pending');
    const referee2 = game.referee2 ?? t('pending');

    if (game.own_referees) {
      const status =
        game.own_referee_1 && game.own_referee_2
          ? 0
          : game.own_referee_1 || game.own_referee_2
          ? 2
          : 1;
      // eigene
      return (
        <Descriptions.Item label={t('referees.plural')} key={'referes'}>
          <InlineEditInput
            highlightingMode="self"
            component={Select}
            value={game.own_referee_1?.id}
            text={referee1}
            loading={savingReferee1}
            onOk={(value) => updateReferee(value, 1)}
            componentProps={{
              options: refereesSelect,
              showSearch: true,
              filterOption: (input: string, option: SelectOption) =>
                option?.label?.toLowerCase().includes(input?.toLowerCase()),
            }}
          />
          <InlineEditInput
            highlightingMode="self"
            component={Select}
            value={game.own_referee_1?.id}
            text={referee2}
            loading={savingReferee2}
            onOk={(value) => updateReferee(value, 2)}
            componentProps={{
              options: refereesSelect,
              showSearch: true,
              filterOption: (input: string, option: SelectOption) =>
                option?.label?.toLowerCase().includes(input?.toLowerCase()),
            }}
          />
          {renderStatusIcon(status)}
        </Descriptions.Item>
      );
    } else {
      // Verband
      return (
        <Descriptions.Item label={t('referees.plural')} key={'referes'}>
          <Space>
            <Typography.Text>{referee1}</Typography.Text>
            <Typography.Text>{referee2}</Typography.Text>
            {renderStatusIcon(0)}
          </Space>
        </Descriptions.Item>
      );
    }
  };

  const renderConflicts = () => {
    if (!game.conflicts) {
      return;
    }

    const conflicts = game.conflicts?.map((user, index) => (
      <Space key={index} direction="vertical">
        <Space>
          <UserAvatar user={user.user} withTooltip />
          <Typography.Text strong>{fullUserName(user.user)}</Typography.Text>
        </Space>
        {user.conflicts.map((conflict, index) => (
          <Space key={index}>
            <Typography.Text>
              {formatDateTime(conflict.date)} |{' '}
            </Typography.Text>
            <Typography.Text>
              {conflict.home} - {conflict.guest}
            </Typography.Text>
          </Space>
        ))}
      </Space>
    ));

    return (
      <Popover content={<Space direction="vertical">{conflicts}</Space>}>
        <FaBolt color={colors.red} size={24} />{' '}
      </Popover>
    );
  };

  const renderTasks = (type: 'home' | 'away') => {
    if (!game.tasks) {
      return;
    }

    const tasks = game.tasks.filter((task) => {
      if (type === 'home' && task.at_home_games) {
        return true;
      } else if (type === 'away' && task.at_away_games) {
        return true;
      }
      return false;
    });

    const returnValue = [];

    for (const task of tasks) {
      const event = {
        assignments: task?.assignments ?? [],
        id: `0_${game.id}`,
        type: TaskEventType.BASKETBALL_GAME,
      } as TaskEvent;

      returnValue.push(
        <Descriptions.Item label={task.name} key={task.id}>
          <Space direction="vertical">
            <Space>
              <FaUsers size={14} />
              <TaskAssignmentSelectComponent
                key={`teams_${task.id}`}
                assignments={task?.assignments ?? []}
                event={event}
                type={TaskableType.CLUB}
                taskId={`club_${task.id}`}
                assignableType={AssignableType.BASKETBALL_TEAM}
                task={task}
              />
            </Space>
            <Space>
              <FaUser size={14} />
              <TaskAssignmentSelectComponent
                assignments={task?.assignments ?? []}
                event={event}
                type={TaskableType.CLUB}
                taskId={`club_${task.id}`}
                assignableType={AssignableType.USER}
                disabled={!task?.allow_interaction}
                task={task}
                checkPosition="back"
              />
            </Space>
          </Space>
        </Descriptions.Item>
      );
    }

    return returnValue;
  };

  const time = formatTime(game.date);
  const awayClass = props.away ? styles['away'] : '';
  const timeTbdClass = time === '00:00' ? styles['time-tbd'] : '';
  const canceledClass =
    props.game.status === BasketballGameStatus.CANCELED
      ? styles['canceled']
      : '';

  const preferredTime = game.preferredData.date ?? game.date;
  //const preferredLocation = game.preferredData.location ?? game.gym;
  /*
  <Typography.Text style={{ fontSize: 10 }}>
    {formatTime(preferredTime)}
  </Typography.Text>
  */

  return (
    <Row
      className={
        styles['container'] +
        ' ' +
        awayClass +
        ' ' +
        timeTbdClass +
        ' ' +
        canceledClass
      }
      align="middle"
      justify="space-between"
      gutter={[0, 16]}
    >
      <Col xs={24} md={4} style={{ textAlign: 'center' }}>
        <Space direction="vertical" size={0} align="center">
          {renderTime(time)}
        </Space>
      </Col>
      <Col xs={24} md={8} style={{ fontSize: 18 }}>
        <Space>
          <Typography.Text>{game.home}</Typography.Text>
          <Typography.Text>-</Typography.Text>
          <Typography.Text>{game.guest}</Typography.Text>
        </Space>
      </Col>
      <Col xs={3} md={1}>
        {renderConflicts()}
      </Col>
      <Col xs={21} md={11}>
        {props.away ? renderAwayContent() : renderHomeContent()}
      </Col>
    </Row>
  );
}
